<!-- ── Page ───────────────────────────────────────────────────────────── -->
<div class="wizard-page">

  <div class="wizard-header">
    <button mat-icon-button type="button" routerLink="/policy-template/create" matTooltip="Back to policy type selection">
      <mat-icon>arrow_back</mat-icon>
    </button>
    <h2 class="wizard-title">New VM Policy Template</h2>
  </div>

  <mat-card class="wizard-card">
    <mat-horizontal-stepper [linear]="true" #stepper (selectionChange)="onStepChange($event)">

      <!-- ══════════════════════════════════════════════════════════════ -->
      <!-- Step 1 — Policy Info                                           -->
      <!-- ══════════════════════════════════════════════════════════════ -->
      <mat-step [stepControl]="infoForm">
        <ng-template matStepLabel>
          <span matTooltip="Give this policy a unique name and an optional description"
                matTooltipPosition="below">Policy Info</span>
        </ng-template>

        <app-step-policy-info [form]="infoForm"></app-step-policy-info>

        <div class="step-actions">
          <span></span>
          <button mat-raised-button color="primary"
                  [disabled]="infoForm.invalid || infoForm.pending"
                  matStepperNext>
            Next <mat-icon iconPositionEnd>arrow_forward</mat-icon>
          </button>
        </div>
      </mat-step>

      <!-- ══════════════════════════════════════════════════════════════ -->
      <!-- Step 2 — Resource Creation                                     -->
      <!-- ══════════════════════════════════════════════════════════════ -->
      <mat-step [stepControl]="creationForm">
        <ng-template matStepLabel>
          <span matTooltip="How VM resources are provisioned for participants"
                matTooltipPosition="below">Resource Creation</span>
        </ng-template>

        <app-step-resource-creation
          [form]="creationForm"
          [description]="vmCreationDescription">
        </app-step-resource-creation>

        <div class="step-actions">
          <button mat-stroked-button matStepperPrevious type="button">
            <mat-icon>arrow_back</mat-icon> Back
          </button>
          <button mat-raised-button color="primary" matStepperNext>
            Next <mat-icon iconPositionEnd>arrow_forward</mat-icon>
          </button>
        </div>
      </mat-step>

      <!-- ══════════════════════════════════════════════════════════════ -->
      <!-- Step 3 — Daily Cleanup                                         -->
      <!-- ══════════════════════════════════════════════════════════════ -->
      <mat-step [stepControl]="dailyCleanupForm">
        <ng-template matStepLabel>
          <span matTooltip="Action to take on VMs at the end of each day during a class"
                matTooltipPosition="below">Daily Cleanup</span>
        </ng-template>

        <app-step-vm-cleanup [form]="dailyCleanupForm" stepLabel="Daily Resource Cleanup"
                             graceConstraint="daily">
        </app-step-vm-cleanup>

        <div class="step-actions">
          <button mat-stroked-button matStepperPrevious type="button">
            <mat-icon>arrow_back</mat-icon> Back
          </button>
          <button mat-raised-button color="primary"
                  [disabled]="dailyCleanupForm.invalid"
                  matStepperNext>
            Next <mat-icon iconPositionEnd>arrow_forward</mat-icon>
          </button>
        </div>
      </mat-step>

      <!-- ══════════════════════════════════════════════════════════════ -->
      <!-- Step 4 — Class End Cleanup                                     -->
      <!-- ══════════════════════════════════════════════════════════════ -->
      <mat-step [stepControl]="classEndCleanupForm">
        <ng-template matStepLabel>
          <span matTooltip="Action to take on VMs when the class ends"
                matTooltipPosition="below">Class End Cleanup</span>
        </ng-template>

        <app-step-vm-cleanup [form]="classEndCleanupForm" stepLabel="Class End Cleanup"
                             graceConstraint="classEnd">
        </app-step-vm-cleanup>

        <div class="step-actions">
          <button mat-stroked-button matStepperPrevious type="button">
            <mat-icon>arrow_back</mat-icon> Back
          </button>
          <button mat-raised-button color="primary"
                  [disabled]="classEndCleanupForm.invalid"
                  matStepperNext>
            Review <mat-icon iconPositionEnd>arrow_forward</mat-icon>
          </button>
        </div>
      </mat-step>

      <!-- ══════════════════════════════════════════════════════════════ -->
      <!-- Step 5 — Review & Save                                         -->
      <!-- ══════════════════════════════════════════════════════════════ -->
      <mat-step>
        <ng-template matStepLabel>
          <span matTooltip="Review your policy configuration before saving"
                matTooltipPosition="below">Review &amp; Save</span>
        </ng-template>

        <!-- Duplicate check spinner -->
        <div class="duplicate-check-loading" *ngIf="isDuplicateChecking">
          <mat-spinner diameter="20"></mat-spinner>
          <span>Checking for duplicate configurations…</span>
        </div>

        <!-- Duplicate warning -->
        <div class="duplicate-warning" *ngIf="duplicatePolicy && !isDuplicateChecking">
          <mat-icon class="warning-icon">warning_amber</mat-icon>
          <div class="warning-text">
            <strong>Duplicate configuration detected.</strong>
            <p>
              This configuration already exists as
              <a class="policy-deeplink" (click)="openExistingPolicy(duplicatePolicy!)">
                {{ duplicatePolicy.policyName }}
              </a>.
              Review the existing policy or go back and modify your configuration.
            </p>
          </div>
        </div>

        <mat-accordion class="review-accordion" [multi]="true">

          <!-- ── Policy Info panel ── -->
          <mat-expansion-panel #panelPI class="review-panel" [expanded]="false">
            <mat-expansion-panel-header>
              <mat-panel-title class="review-panel-title">
                <mat-icon class="review-panel-icon">label</mat-icon>
                Policy Info
              </mat-panel-title>
              <mat-panel-description *ngIf="!panelPI.expanded">
                <div class="review-panel-summary">
                  <span class="rps-item"><mat-icon class="rps-icon">label</mat-icon>{{ reviewPolicyName }}</span>
                </div>
              </mat-panel-description>
            </mat-expansion-panel-header>
            <div class="review-grid">
              <span class="review-label"><mat-icon class="rl-icon">label</mat-icon>Name</span>
              <span class="review-value">{{ reviewPolicyName }}</span>
              <span class="review-label"><mat-icon class="rl-icon">description</mat-icon>Description</span>
              <span class="review-value">{{ reviewDescription || '—' }}</span>
            </div>
          </mat-expansion-panel>

          <!-- ── Resource Creation panel ── -->
          <mat-expansion-panel #panelRC class="review-panel" [expanded]="false">
            <mat-expansion-panel-header>
              <mat-panel-title class="review-panel-title">
                <mat-icon class="review-panel-icon">add_circle_outline</mat-icon>
                Resource Creation
              </mat-panel-title>
              <mat-panel-description *ngIf="!panelRC.expanded">
                <div class="review-panel-summary">
                  <span class="rps-item"><mat-icon class="rps-icon">flash_on</mat-icon>On Demand</span>
                </div>
              </mat-panel-description>
            </mat-expansion-panel-header>
            <div class="review-grid">
              <span class="review-label"><mat-icon class="rl-icon">flash_on</mat-icon>Mode</span>
              <span class="review-value">On Demand</span>
            </div>
          </mat-expansion-panel>

          <!-- ── Daily Cleanup panel ── -->
          <mat-expansion-panel #panelDC class="review-panel" [expanded]="false">
            <mat-expansion-panel-header>
              <mat-panel-title class="review-panel-title">
                <mat-icon class="review-panel-icon">today</mat-icon>
                Daily Cleanup
              </mat-panel-title>
              <mat-panel-description *ngIf="!panelDC.expanded">
                <div class="review-panel-summary">
                  <span class="rps-item"><mat-icon class="rps-icon">cleaning_services</mat-icon>{{ formatCleanupAction(dailyCleanupForm, 'daily') }}</span>
                </div>
              </mat-panel-description>
            </mat-expansion-panel-header>
            <div class="review-grid">
              <span class="review-label"><mat-icon class="rl-icon">cleaning_services</mat-icon>Action</span>
              <span class="review-value">{{ formatCleanupAction(dailyCleanupForm, 'daily') }}</span>
            </div>
          </mat-expansion-panel>

          <!-- ── Class End Cleanup panel ── -->
          <mat-expansion-panel #panelCC class="review-panel" [expanded]="false">
            <mat-expansion-panel-header>
              <mat-panel-title class="review-panel-title">
                <mat-icon class="review-panel-icon">event_available</mat-icon>
                Class End Cleanup
              </mat-panel-title>
              <mat-panel-description *ngIf="!panelCC.expanded">
                <div class="review-panel-summary">
                  <span class="rps-item"><mat-icon class="rps-icon">cleaning_services</mat-icon>{{ formatCleanupAction(classEndCleanupForm) }}</span>
                </div>
              </mat-panel-description>
            </mat-expansion-panel-header>
            <div class="review-grid">
              <span class="review-label"><mat-icon class="rl-icon">cleaning_services</mat-icon>Action</span>
              <span class="review-value">{{ formatCleanupAction(classEndCleanupForm) }}</span>
            </div>
          </mat-expansion-panel>

        </mat-accordion>

        <div class="step-actions">
          <button mat-stroked-button matStepperPrevious type="button">
            <mat-icon>arrow_back</mat-icon> Back
          </button>
          <button mat-raised-button color="primary"
                  [disabled]="isSubmitting || isDuplicateChecking || duplicatePolicy !== null"
                  (click)="submit()">
            <mat-progress-spinner *ngIf="isSubmitting || isDuplicateChecking"
                                  mode="indeterminate" diameter="18"
                                  class="btn-spinner">
            </mat-progress-spinner>
            {{ isDuplicateChecking ? 'Checking…' : isSubmitting ? 'Saving…' : 'Save Policy Template' }}
          </button>
        </div>

      </mat-step>

    </mat-horizontal-stepper>
  </mat-card>

</div>

<!-- Duplicate policy drawer (D-10) -->
<app-duplicate-policy-drawer
  [policySummary]="drawerPolicy"
  (closed)="drawerPolicy = null">
</app-duplicate-policy-drawer>
